import './RegisterView.css'
import '../components/SocialAuthButtons.css'

import { useEffect, useState } from 'react'
import { Link, Navigate, useNavigate } from 'react-router'
import { useAtomValue, useSetAtom } from 'jotai'

import type { FormValidator } from '../components/FormValidators'

import { getRejectionMessage } from '../lib/rejectionMessages'
import { session, waitlist } from '../store'

import { Button } from '../components/Button'
import { CityAutocomplete, CityResult } from '../components/CityAutocomplete'
import { FieldError } from '../components/FieldError'
import { Form } from '../components/Form'
import { Label } from '../components/Label'
import { SocialAuthButtons } from '../components/SocialAuthButtons'
import { TextField } from '../components/TextField'
import { useFormFieldErrors } from '../hooks/useFormFieldErrors'
import { isAnySocialAuthConfigured, type SocialAuthTokens } from '../lib/social-auth'

type RegistrationFormValue = {
  email: string
  password: string
  confirmPassword: string
}

type WaitlistFormValue = {
  email: string
}

const validatePasswordMatch: FormValidator<RegistrationFormValue> = (value) => {
  if (value.password && value.confirmPassword && value.password !== value.confirmPassword) {
    return { confirmPassword: 'mismatch' }
  }
  return { confirmPassword: [] }
}

export function RegisterView() {
  const [showWaitlist, setShowWaitlist] = useState(false)
  const [isCheckingRegion, setIsCheckingRegion] = useState(false)
  const [location, setLocation] = useState<CityResult | null>(null)
  const [locationError, setLocationError] = useState<string | null>(null)
  const [socialError, setSocialError] = useState<string | null>(null)
  const [linkPassword, setLinkPassword] = useState('')
  const [oauthWaitlistEntry, setOauthWaitlistEntry] = useState<{
    id: string
    email: string
    location: string
    notifiedAt?: string
    createdAt: string
  } | null>(null)
  const [registrationForm, setRegistrationForm] = useState<RegistrationFormValue>({
    email: '',
    password: '',
    confirmPassword: '',
  })
  const [waitlistForm, setWaitlistForm] = useState<WaitlistFormValue>({ email: '' })

  const isLoading = useAtomValue(session.registerIsLoadingAtom)
  const loginIsLoading = useAtomValue(session.loginIsLoadingAtom)
  const registerSuccess = useAtomValue(session.registerSuccessAtom)
  const authStatus = useAtomValue(session.sessionStatusAtom)
  const waitlistIsLoading = useAtomValue(waitlist.waitlistIsLoadingAtom)
  const waitlistEntry = useAtomValue(waitlist.waitlistEntryAtom)
  const passwordLink = useAtomValue(session.passwordLinkRequiredAtom)
  const rejectionError = useAtomValue(session.loginRejectionErrorAtom)

  const sessionRegister = useSetAtom(session.registerAtom)
  const oauthRegister = useSetAtom(session.oauthRegisterAtom)
  const sessionLogin = useSetAtom(session.loginAtom)
  const clearPasswordLink = useSetAtom(session.clearPasswordLinkAtom)
  const resetRegister = useSetAtom(session.resetRegisterAtom)
  const checkRegionStatus = useSetAtom(waitlist.checkRegionStatusAtom)
  const joinWaitlist = useSetAtom(waitlist.joinWaitlistAtom)
  const resetWaitlist = useSetAtom(waitlist.resetWaitlistAtom)
  const navigate = useNavigate()

  const serverErrors = useFormFieldErrors(session.registerIssuesAtom)
  const loginErrors = useFormFieldErrors(session.loginIssuesAtom)
  const waitlistErrors = useFormFieldErrors(waitlist.waitlistIssuesAtom)
  const socialConfigured = isAnySocialAuthConfigured()

  useEffect(() => {
    return () => {
      resetWaitlist()
      resetRegister()
    }
  }, [])

  if (authStatus === 'authenticated') {
    return <Navigate to="/profile" replace />
  }

  useEffect(() => {
    if (!location) {
      setShowWaitlist(false)
      return
    }

    let cancelled = false
    setLocationError(null)
    setIsCheckingRegion(true)

    checkRegionStatus({
      osmId: location.osmId,
      osmType: location.osmType,
      latitude: location.latitude,
      longitude: location.longitude,
      displayName: location.displayName,
      country: location.country,
    }).then((result) => {
      if (cancelled) return
      setIsCheckingRegion(false)

      if (!result.success) {
        setLocationError('Unable to check your region. Please try again.')
        return
      }

      setShowWaitlist(!result.isActive)
    })

    return () => {
      cancelled = true
    }
  }, [location])

  const handleRegistrationSubmit = () => {
    if (!location) {
      setLocationError('Please select a city')
      return
    }

    sessionRegister({
      email: registrationForm.email,
      password: registrationForm.password,
    })
  }

  const handleWaitlistSubmit = async () => {
    if (!location) {
      setLocationError('Please select a city')
      return
    }

    await joinWaitlist(waitlistForm.email)
  }

  const requireLocation = () => {
    if (!location) {
      setLocationError('Please select a city first')
      return false
    }
    return true
  }

  const handleSocialTokens = async (tokens: SocialAuthTokens) => {
    setSocialError(null)
    if (!requireLocation()) return

    clearPasswordLink()
    setLinkPassword('')

    const result = await oauthRegister({
      provider: tokens.provider,
      idToken: tokens.idToken,
      accessToken: tokens.accessToken,
      osmId: location!.osmId,
      osmType: location!.osmType,
      latitude: location!.latitude,
      longitude: location!.longitude,
      location: location!.displayName,
      country: location!.country,
    })

    if (result.success && result.kind === 'session') {
      navigate('/profile', { replace: true })
      return
    }

    if (result.success && result.kind === 'waitlist') {
      setOauthWaitlistEntry(result.entry)
      return
    }

    if (result.passwordLinkRequired) return

    setSocialError('Social sign-up failed — please try again.')
  }

  const handleLinkPasswordSubmit = async () => {
    if (!passwordLink || !linkPassword) return
    setSocialError(null)

    if (passwordLink.flow === 'login' || !location) {
      const result = await sessionLogin(
        passwordLink.provider === 'google'
          ? {
              type: 'google',
              idToken: passwordLink.idToken,
              accessToken: passwordLink.accessToken,
              password: linkPassword,
            }
          : {
              type: 'facebook',
              accessToken: passwordLink.accessToken!,
              password: linkPassword,
            },
      )
      if (result.success) navigate('/profile', { replace: true })
      return
    }

    const result = await oauthRegister({
      provider: passwordLink.provider,
      idToken: passwordLink.idToken,
      accessToken: passwordLink.accessToken,
      password: linkPassword,
      osmId: location.osmId,
      osmType: location.osmType,
      latitude: location.latitude,
      longitude: location.longitude,
      location: location.displayName,
      country: location.country,
    })

    if (result.success && result.kind === 'session') {
      navigate('/profile', { replace: true })
      return
    }

    if (result.success && result.kind === 'waitlist') {
      setOauthWaitlistEntry(result.entry)
    }
  }

  const handleCancelLink = () => {
    clearPasswordLink()
    setLinkPassword('')
  }

  const isBusy = isLoading || loginIsLoading || isCheckingRegion || waitlistIsLoading
  const resolvedWaitlistEntry = oauthWaitlistEntry ?? waitlistEntry

  // Show success message after registration
  if (registerSuccess) {
    return (
      <div className="register-view register-view--success">
        <div className="register-view__success-icon">✓</div>
        <h2 className="register-view__title">Check your email</h2>
        <p className="register-view__message">
          We've sent a verification link to <strong>{registrationForm.email}</strong>. Click the
          link to complete your registration.
        </p>
        <p className="register-view__message register-view__message--secondary">
          Didn't receive the email? Check your spam folder or{' '}
          <Link to="/register" onClick={() => resetRegister()}>
            try again
          </Link>
          .
        </p>
      </div>
    )
  }

  // Show success message after joining waitlist
  if (resolvedWaitlistEntry) {
    const regionAlreadyActive = !!resolvedWaitlistEntry.notifiedAt
    return (
      <div className="register-view register-view--success">
        <div className="register-view__success-icon">✓</div>
        <h2 className="register-view__title">
          {regionAlreadyActive ? 'Check your email' : "You're on the list!"}
        </h2>
        <p className="register-view__message">
          {regionAlreadyActive ? (
            <>
              Your area is already active. We emailed <strong>{resolvedWaitlistEntry.email}</strong>{' '}
              a link to complete your application.
            </>
          ) : (
            <>
              We&apos;ll email you at <strong>{resolvedWaitlistEntry.email}</strong> as soon as your
              area becomes active.
            </>
          )}
        </p>
      </div>
    )
  }

  return (
    <div className="register-view">
      <h2 className="register-view__title">Join Revel</h2>

      {rejectionError && (
        <div className="register-view__error-banner">
          {getRejectionMessage(rejectionError.code, rejectionError.daysRemaining)}
        </div>
      )}

      {socialError && <div className="register-view__error-banner">{socialError}</div>}

      <div className="register-view__location-field">
        <Label>Your City</Label>
        <CityAutocomplete
          placeholder="Search for your city..."
          value={location}
          onChange={setLocation}
          disabled={isBusy}
          hasError={!!locationError}
        />
        {locationError && <p className="register-view__location-error">{locationError}</p>}
      </div>

      {passwordLink && (
        <div className="social-auth-link-panel">
          <p className="social-auth-link-panel__title">Confirm your password</p>
          <p className="social-auth-link-panel__message">
            An account already exists for <strong>{passwordLink.email}</strong>. Enter your password
            to link {passwordLink.provider === 'google' ? 'Google' : 'Facebook'} and continue.
          </p>
          <Form
            value={{ password: linkPassword }}
            onChange={(value) => setLinkPassword(value.password)}
            onSubmit={handleLinkPasswordSubmit}
            flags={{ ...serverErrors, ...loginErrors }}
          >
            <TextField
              name="password"
              label="Password"
              kind="password"
              placeholder="Enter your password"
              required
              disabled={isBusy}
            />
            <FieldError
              name="password"
              text={{
                required: 'Password is required',
                invalidCredentials: 'Password is incorrect',
              }}
            />
            <div className="social-auth-link-panel__actions">
              <Button type="submit" kind="primary" disabled={isBusy || !linkPassword}>
                {isBusy ? 'Linking…' : 'Link and continue'}
              </Button>
              <Button kind="ghost" disabled={isBusy} onClick={handleCancelLink}>
                Cancel
              </Button>
            </div>
          </Form>
        </div>
      )}

      {socialConfigured && !passwordLink && (
        <>
          <SocialAuthButtons
            mode="sign-up"
            disabled={isBusy || !location}
            onTokens={handleSocialTokens}
            onError={setSocialError}
          />
          <div className="social-auth-divider">or</div>
        </>
      )}

      <Form
        className={`register-view__form ${showWaitlist ? 'register-view__form--hidden' : ''}`}
        value={registrationForm}
        onChange={setRegistrationForm}
        onSubmit={handleRegistrationSubmit}
        flags={serverErrors}
        validate={validatePasswordMatch}
      >
        <TextField
          name="email"
          label="Email"
          placeholder="Enter your email"
          required
          email
          disabled={isBusy}
        />
        <FieldError
          name="email"
          text={{
            required: 'Email is required',
            email: 'Please enter a valid email',
            networkError: 'Network error. Please try again.',
            emailAlreadyInUse: 'This email is already registered',
          }}
        />

        <TextField
          name="password"
          label="Password"
          kind="password"
          placeholder="Create a password"
          required
          min={8}
          disabled={isBusy}
        />
        <FieldError
          name="password"
          text={{
            required: 'Password is required',
            min: 'Password must be at least 8 characters',
            tooShort: 'Password must be at least 8 characters',
            tooLong: 'Password is too long',
          }}
        />

        <TextField
          name="confirmPassword"
          label="Confirm Password"
          kind="password"
          placeholder="Confirm your password"
          required
          disabled={isBusy}
        />
        <FieldError
          name="confirmPassword"
          text={{
            required: 'Please confirm your password',
            mismatch: 'Passwords do not match',
          }}
        />
        <FieldError
          name="provider"
          text={{
            invalidToken: 'Social sign-up failed — please try again',
            registerFailed: 'Unable to sign up — please try again',
          }}
        />
        <FieldError
          name="location"
          text={{
            required: 'Please select a city',
          }}
        />

        <div className="register-view__actions">
          <Button type="submit" kind="primary" disabled={isBusy || !location}>
            {isLoading ? 'Submitting...' : 'Apply'}
          </Button>
          <Link to="/login" className="register-view__alt-action">
            Sign in
          </Link>
        </div>
      </Form>

      <Form
        className={`register-view__form ${showWaitlist ? 'register-view__form--visible' : 'register-view__form--hidden'}`}
        value={waitlistForm}
        onChange={setWaitlistForm}
        onSubmit={handleWaitlistSubmit}
        flags={waitlistErrors}
      >
        <p className="register-view__waitlist-info">
          We're not yet active in your area, but we're growing fast! Join the waitlist and we'll
          notify you as soon as {location?.displayName || 'your area'} opens up.
        </p>

        <TextField
          name="email"
          label="Email"
          placeholder="Enter your email"
          required
          email
          disabled={isBusy}
        />
        <FieldError
          name="email"
          text={{
            required: 'Email is required',
            email: 'Please enter a valid email',
            networkError: 'Network error. Please try again.',
          }}
        />

        <div className="register-view__actions">
          <Button type="submit" kind="primary" disabled={isBusy || !location}>
            {waitlistIsLoading ? 'Joining...' : 'Join Waitlist'}
          </Button>
          <Link to="/login" className="register-view__alt-action">
            Sign in
          </Link>
        </div>
      </Form>
    </div>
  )
}
